import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import FormRadio, { type FormRadioProps } from '../FormRadio';
import type { Language } from '@/chassis/ui/i18n/i18n-helper';
import { useLanguage } from '@/chassis/ui/i18n/useLanguage';

export type FormRadioOption = {
    value: FormRadioProps['value'];
    label?: FormRadioProps['label'];
    i18nLabel?: FormRadioProps['i18nLabel'];
    disabled?: boolean;
};

export interface FormRadioGroupProps extends Omit<ComponentPropsWithoutRef<'div'>, 'onChange'> {
    name: string;
    value?: FormRadioProps['value'];
    options: FormRadioOption[];
    onChange?: FormRadioProps['onChange'];
    variant?: FormRadioProps['variant'];
    optionsClassName?: string;
    optionWrapperClassName?: FormRadioProps['wrapperClassName'];
    label?: ReactNode;
    labelClassName?: string;
    i18nLabel?: Record<Language, string> | string;
    disabled?: boolean;
}

const FormRadioGroup = forwardRef<HTMLDivElement, FormRadioGroupProps>(function FormRadioGroup(
    {
        name,
        value,
        options,
        onChange,
        variant = 'inline',
        className,
        label,
        i18nLabel,
        labelClassName,
        optionsClassName = 'grid grid-cols-1 gap-2',
        optionWrapperClassName,
        disabled,
        ...rest
    },
    ref,
) {
    const lang = useLanguage();
    const displayLabel = typeof i18nLabel === 'string' ? i18nLabel : i18nLabel?.[lang];
    return (
        <div ref={ref} className={twMerge(['space-y-2', className])} {...rest}>
            {displayLabel || label ? <div className={twMerge(['text-sm font-medium text-slate-800 dark:text-slate-100', labelClassName])}>{displayLabel ?? label}</div> : null}
            <div className={twMerge([optionsClassName])}>
                {options.map((option) => (
                    <FormRadio
                        key={String(option.value)}
                        id={`${name}-${String(option.value)}`}
                        name={name}
                        value={option.value}
                        label={option.label}
                        i18nLabel={option.i18nLabel}
                        variant={variant}
                        wrapperClassName={optionWrapperClassName}
                        checked={String(value ?? '') === String(option.value)}
                        onChange={onChange}
                        disabled={disabled || option.disabled}
                    />
                ))}
            </div>
        </div>
    );
});

export default FormRadioGroup;
